import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  NavLink as RouterNavLink,
} from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import {
  MapPin,
  User,
  BarChart2,
  Shield,
  HeartHandshake,
  Info,
  HelpingHand,
  Menu,
  X,
  ChevronDown,
} from "lucide-react";

import Home from "./Home";
import Map from "./Map";
import Directory from "./Directory";
import Events from "./Events";
import EventDetailPage from "./EventDetailPage";
import BusinessNetworkHub from "./BusinessNetworkHub";
import BusinessNetworkLaunch from "./BusinessNetworkLaunch";
import BusinessNetworkRegistrationsAdmin from "./BusinessNetworkRegistrationsAdmin";
import HousingDevelopmentHub from "./housingDevelopment/HousingDevelopmentHub";
import HousingDevelopmentSurvey from "./housingDevelopment/HousingDevelopmentSurvey";
import HousingDevelopmentAdmin from "./housingDevelopment/admin/HousingDevelopmentAdmin";
import Community from "./Community";
import UserProfile from "./UserProfile";
import Insights from "./Insights";
import Admin from "./Admin";
import AdminLogin from "./AdminLogin";
import RequireAdmin from "./RequireAdmin";
import FoodBank from "./FoodBank";
import About from "./About";
import GetInvolved from "./GetInvolved";
import PrivacyNotice from "./PrivacyNotice";
import CommunityVoice from "./communityVoice/CommunityVoice";
import SurveyPage from "./communityVoice/SurveyPage";
import AiDigitalSkillsSurvey from "./communityVoice/AiDigitalSkillsSurvey";
import AdminDashboard from "./communityVoice/AdminDashboard";
import AiDigitalSkillsAdmin from "./communityVoice/AiDigitalSkillsAdmin";
import TokenChecker from "./communityVoice/TokenChecker";
import PhoenixHubLanding from "./phoenixHub/PhoenixHubLanding";
import PhoenixSpaces from "./phoenixHub/PhoenixSpaces";
import PhoenixBookingForm from "./phoenixHub/PhoenixBookingForm";
import PhoenixBookingSuccess from "./phoenixHub/PhoenixBookingSuccess";
import PhoenixBookings from "./phoenixHub/admin/PhoenixBookings";
import PhoenixBookingDetail from "./phoenixHub/admin/PhoenixBookingDetail";
import PhoenixCalendar from "./phoenixHub/admin/PhoenixCalendar";
import PhoenixSpacesAdmin from "./phoenixHub/admin/PhoenixSpacesAdmin";
import PhoenixBlockedPeriods from "./phoenixHub/admin/PhoenixBlockedPeriods";
import CommunityInformationAdmin from "./communityInformation/CommunityInformationAdmin";
import { COMMUNITY_INFORMATION_AGENT_ENABLED } from "./communityInformation/config";
import CanonicalUrl from "./CanonicalUrl";
import NotFound from "./NotFound";
import { VISITOR_PRIMARY_NAV_ITEMS, VISITOR_SECONDARY_NAV_ITEMS } from "./navigation";

const secondaryNavIcons = {
  info: Info,
  helpingHand: HelpingHand,
  heartHandshake: HeartHandshake,
  user: User,
  insights: BarChart2,
};

function NavLink({ to, children, featured = false, onClick }) {
  return (
    <RouterNavLink
      to={to}
      end={to === "/"}
      onClick={onClick}
      className={({ isActive }) =>
        `px-2 py-2 xl:px-3 rounded-full text-sm xl:text-base transition ${
          isActive
            ? "bg-white/15 text-white font-semibold"
            : featured
            ? "bg-[#E63946] text-white font-bold shadow-sm hover:bg-[#d62f3d]"
            : "text-white/80 hover:bg-white/10 hover:text-white"
        }`
      }
    >
      {children}
    </RouterNavLink>
  );
}

export default function App() {
  const [theme, setTheme] = useState("default"); // default | dark | high
  const [fontSize, setFontSize] = useState("normal"); // normal | large
  const [moreOpen, setMoreOpen] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAuthUser(user || null);
    });
    return () => unsub();
  }, []);

  const mainThemeClasses =
    theme === "dark"
      ? "bg-black text-white"
      : theme === "high"
      ? "bg-black text-white contrast-125"
      : "bg-gradient-to-b from-[#0B1120] to-[#1B263B] text-white";

  const fontClasses = fontSize === "large" ? "text-lg" : "text-base";

  return (
    <Router>
      <CanonicalUrl />
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <main
        onClick={() => {
          if (moreOpen) setMoreOpen(false);
          if (mobileOpen) setMobileOpen(false);
        }}
        className={`min-h-screen flex flex-col ${mainThemeClasses} ${fontClasses}`}
      >
        {/* Top bar with search + accessibility controls */}
        <header className="border-b border-white/10 bg-black/20 backdrop-blur">
          <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-[#ff7a84]" aria-hidden="true" />
              <span className="font-semibold">
                Serving the Isle of Dogs and neighbouring Tower Hamlets communities
              </span>
            </div>

            <div className="flex flex-wrap gap-3 items-center justify-end">
              {/* Accessibility tools */}
              <label className="flex items-center gap-1 text-xs md:text-sm">
                <span>A:</span>
                <select
                  value={fontSize}
                  onChange={(e) => setFontSize(e.target.value)}
                  className="bg-black/40 border border-white/20 rounded px-2 py-1 text-xs md:text-sm"
                >
                  <option value="normal">Standard font</option>
                  <option value="large">Large font</option>
                </select>
              </label>

              <label className="flex items-center gap-1 text-xs md:text-sm">
                <span>Theme:</span>
                <select
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  className="bg-black/40 border border-white/20 rounded px-2 py-1 text-xs md:text-sm"
                >
                  <option value="default">Standard</option>
                  <option value="dark">Dark</option>
                  <option value="high">High contrast</option>
                </select>
              </label>
            </div>
          </div>

          {/* Primary navigation */}
          <nav className="border-t border-white/10" aria-label="Primary navigation">
            <div className="max-w-6xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Link to="/" className="flex items-center gap-2">
                  <img
                    src="/logo.png"
                    alt="Urban Unity Network"
                    className="w-8 h-8"
                  />
                  <span className="font-semibold text-sm md:text-base">UUN</span>
                </Link>

                <button
                  type="button"
                  className="xl:hidden inline-flex items-center justify-center p-2 rounded-lg hover:bg-white/10 transition"
                  onClick={() => setMobileOpen((v) => !v)}
                  aria-label="Toggle menu"
                  aria-expanded={mobileOpen}
                  aria-controls="mobile-navigation"
                >
                  {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
              </div>

              {/* Desktop nav */}
              <div className="hidden xl:flex gap-1 xl:gap-2 items-center">
                {VISITOR_PRIMARY_NAV_ITEMS.map((item) => (
                  <NavLink key={item.to} to={item.to} featured={item.featured}>
                    {item.label}
                  </NavLink>
                ))}

                <div className="relative">
                  <button
                    type="button"
                    aria-expanded={moreOpen}
                    aria-haspopup="menu"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMoreOpen((v) => !v);
                    }}
                    className="px-2 py-2 xl:px-3 rounded-full text-sm xl:text-base hover:bg-white/10 transition inline-flex items-center gap-1"
                  >
                    More <ChevronDown className="w-4 h-4 opacity-80" />
                  </button>

                  {moreOpen && (
                    <div
                      className="absolute right-0 mt-2 w-56 bg-black/90 border border-white/10 rounded-xl shadow-xl p-2 z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {VISITOR_SECONDARY_NAV_ITEMS.map((item, index) => {
                        const Icon = secondaryNavIcons[item.icon];
                        return (
                          <React.Fragment key={item.to}>
                            {index === 3 ? <div className="h-px bg-white/10 my-2" /> : null}
                            <Link
                              to={item.to}
                              onClick={() => setMoreOpen(false)}
                              className="block px-3 py-2 rounded-lg hover:bg-white/10 text-sm"
                            >
                              <span className="inline-flex items-center gap-2">
                                <Icon className="w-4 h-4" /> {item.label}
                              </span>
                            </Link>
                          </React.Fragment>
                        );
                      })}

                      {authUser && (
                        <Link
                          to="/admin"
                          onClick={() => setMoreOpen(false)}
                          className="block px-3 py-2 rounded-lg hover:bg-white/10 text-sm"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Shield className="w-4 h-4" /> Admin Dashboard
                          </span>
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile nav */}
            {mobileOpen && (
              <div id="mobile-navigation" className="xl:hidden border-t border-white/10">
                <div className="max-w-6xl mx-auto px-4 py-3 space-y-2">
                  <div className="flex flex-col gap-2">
                    {VISITOR_PRIMARY_NAV_ITEMS.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        featured={item.featured}
                        onClick={() => setMobileOpen(false)}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>

                  <div className="h-px bg-white/10 my-2" />

                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {VISITOR_SECONDARY_NAV_ITEMS.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm"
                      >
                        {item.label}
                      </Link>
                    ))}

                    {authUser && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileOpen(false)}
                        className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm"
                      >
                        Admin
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
          </nav>
        </header>

        {/* Page content */}
        <section id="main-content" tabIndex="-1" className="flex-1 outline-none">
          <div className="max-w-6xl mx-auto px-4 py-8">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/get-involved" element={<GetInvolved />} />
              <Route path="/privacy" element={<PrivacyNotice />} />
              <Route path="/map" element={<Map />} />
              <Route path="/events" element={<Events />} />
              <Route path="/events/:eventReference" element={<EventDetailPage />} />
              <Route path="/business-network" element={<BusinessNetworkHub />} />
              <Route path="/business-network-launch" element={<BusinessNetworkLaunch />} />
              <Route path="/housing-development" element={<HousingDevelopmentHub />} />
              <Route path="/housing-development/surveys/:surveySlug" element={<HousingDevelopmentSurvey />} />
              <Route path="/directory" element={<Directory />} />
              <Route path="/community" element={<Community />} />
              <Route path="/food-bank" element={<FoodBank />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/insights" element={<Insights />} />

              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/community-voice" element={<CommunityVoice />} />
              <Route path="/community-voice/barkantine-festival-2026" element={<SurveyPage />} />
              <Route path="/community-voice/ai-digital-skills" element={<AiDigitalSkillsSurvey />} />
              <Route path="/phoenix-hub" element={<PhoenixHubLanding />} />
              <Route path="/phoenix-hub/spaces" element={<PhoenixSpaces />} />
              <Route path="/phoenix-hub/book" element={<PhoenixBookingForm />} />
              <Route path="/phoenix-hub/booking-success" element={<PhoenixBookingSuccess />} />
              <Route path="/admin/community-voice" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
              <Route path="/admin/community-voice/ai-digital-skills" element={<RequireAdmin><AiDigitalSkillsAdmin /></RequireAdmin>} />
              <Route path="/admin/community-voice/check-token" element={<RequireAdmin><TokenChecker /></RequireAdmin>} />
              <Route path="/admin/phoenix-bookings" element={<RequireAdmin><PhoenixBookings /></RequireAdmin>} />
              <Route path="/admin/phoenix-bookings/:id" element={<RequireAdmin><PhoenixBookingDetail /></RequireAdmin>} />
              <Route path="/admin/phoenix-calendar" element={<RequireAdmin><PhoenixCalendar /></RequireAdmin>} />
              <Route path="/admin/phoenix-spaces" element={<RequireAdmin><PhoenixSpacesAdmin /></RequireAdmin>} />
              <Route path="/admin/phoenix-blocked-dates" element={<RequireAdmin><PhoenixBlockedPeriods /></RequireAdmin>} />
              <Route path="/admin/business-network-registrations" element={<RequireAdmin><BusinessNetworkRegistrationsAdmin /></RequireAdmin>} />
              <Route path="/admin/housing-development" element={<RequireAdmin><HousingDevelopmentAdmin /></RequireAdmin>} />
              {COMMUNITY_INFORMATION_AGENT_ENABLED ? (
                <Route
                  path="/admin/community-information"
                  element={<RequireAdmin><CommunityInformationAdmin /></RequireAdmin>}
                />
              ) : null}
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <Admin />
                  </RequireAdmin>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 bg-black/30 backdrop-blur mt-8">
          <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-xs md:text-sm text-gray-300">
                Connected via WhatsApp groups across the Isle of Dogs.
              </span>
            </div>

            <nav aria-label="Footer navigation" className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
              <Link className="text-white/75 underline-offset-4 hover:text-white hover:underline" to="/about">About</Link>
              <Link className="text-white/75 underline-offset-4 hover:text-white hover:underline" to="/get-involved">Get involved</Link>
              <Link className="text-white/75 underline-offset-4 hover:text-white hover:underline" to="/directory">Directory</Link>
              <Link className="text-white/75 underline-offset-4 hover:text-white hover:underline" to="/business-network">Business Network</Link>
              <Link className="text-white/75 underline-offset-4 hover:text-white hover:underline" to="/housing-development">Housing &amp; Development</Link>
              <Link className="text-white/75 underline-offset-4 hover:text-white hover:underline" to="/privacy">Privacy</Link>
              <a className="text-white/75 underline-offset-4 hover:text-white hover:underline" href="mailto:info@urbanunitynetwork.com">Contact UUN</a>
            </nav>

            <p className="text-xs md:text-sm text-gray-400">
              © {new Date().getFullYear()} Urban Unity Network. All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </Router>
  );
}
